import styled from 'styled-components'
import { SPACING, BORDER_RADIUS } from '../../../styles/theme'
import { SkeletonBox } from '../styles/SkeletonStyles'
import { ReportContainer, ReportHeader } from '../../../Pages/styles/ReportPageStyles'

const OptionsCard = styled.div`
  border: 1px solid var(--color-border-card);
  border-radius: ${BORDER_RADIUS.xl};
  margin-bottom: 24px;
  overflow: hidden;
`

const OptionsCardHeader = styled.div`
  padding: ${SPACING.sm} ${SPACING.md};
  border-bottom: 1px solid var(--color-border-card);
  background: var(--color-bg-secondary);
`

const OptionsCardBody = styled.div`
  padding: ${SPACING.md};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

const SectionBlock = styled.div`
  margin-bottom: ${SPACING.xxxl};
`

const SectionDividerSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  margin: ${SPACING.xl} 0 ${SPACING.lg};

  &::before {
    content: '';
    flex: 0 0 16px;
    height: 1px;
    background: var(--color-border-dark);
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-border);
  }
`

const ContentBlock = styled.div<{ $accentColor: string }>`
  border-left: 3px solid ${({ $accentColor }) => $accentColor};
  border-radius: 0 ${BORDER_RADIUS.xl} ${BORDER_RADIUS.xl} 0;
  background: var(--color-bg-card);
  border-top: 1px solid var(--color-border-card);
  border-right: 1px solid var(--color-border-card);
  border-bottom: 1px solid var(--color-border-card);
  padding: ${SPACING.lg};
  margin-bottom: ${SPACING.xl};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

const ChildBlock = styled.div`
  margin-left: 10px;
  padding-left: ${SPACING.lg};
  border-left: 1px solid var(--color-border-dark);
  margin-top: ${SPACING.md};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

// ─── Sections to render ───────────────────────────────────────────────────────

const SECTIONS = [
  {
    label: '20px',
    color: '#3b82f6',
    blocks: [{ lines: [55, 35, 80, 65] }, { lines: [45, 30, 70] }],
  },
  { label: '16px', color: '#10b981', blocks: [{ lines: [60, 40, 85] }, { lines: [50, 35] }] },
  { label: '14px', color: '#f59e0b', blocks: [{ lines: [70, 45] }, { lines: [55, 38] }] },
]

export function SkeletonReport() {
  return (
    <ReportContainer>
      {/* Header */}
      <ReportHeader>
        <SkeletonBox $width="280px" $height="36px" $radius={BORDER_RADIUS.lg} />
        <SkeletonBox $width="130px" $height="32px" $radius={BORDER_RADIUS.xl} />
      </ReportHeader>

      {/* Presentation Options card */}
      <OptionsCard>
        <OptionsCardHeader>
          <SkeletonBox $width="160px" $height="18px" $radius={BORDER_RADIUS.sm} />
        </OptionsCardHeader>
        <OptionsCardBody>
          <SkeletonBox $width="200px" $height="16px" $radius={BORDER_RADIUS.sm} />
          <SkeletonBox $width="260px" $height="16px" $radius={BORDER_RADIUS.sm} />
          <SkeletonBox $width="180px" $height="16px" $radius={BORDER_RADIUS.sm} />
        </OptionsCardBody>
      </OptionsCard>

      {/* Content sections */}
      {SECTIONS.map((section, si) => (
        <SectionBlock key={si}>
          {/* Section divider */}
          <SectionDividerSkeleton>
            <SkeletonBox $width={section.label} $height="10px" $radius={BORDER_RADIUS.sm} />
          </SectionDividerSkeleton>

          {/* Content blocks */}
          {section.blocks.map((block, bi) => (
            <ContentBlock key={bi} $accentColor={section.color}>
              {block.lines.map((pct, li) => (
                <SkeletonBox
                  key={li}
                  $width={`${pct}%`}
                  $height="14px"
                  $radius={BORDER_RADIUS.sm}
                />
              ))}
              {/* Child items */}
              <ChildBlock>
                <SkeletonBox $width="75%" $height="13px" $radius={BORDER_RADIUS.sm} />
                <SkeletonBox $width="60%" $height="13px" $radius={BORDER_RADIUS.sm} />
              </ChildBlock>
            </ContentBlock>
          ))}
        </SectionBlock>
      ))}
    </ReportContainer>
  )
}
